<template>
  <div class="drawer-activity-types extra-padding-x">

    <h1 class="drawer-header">
      <template v-if="!dbReady">Loading activity types...</template>
      <template v-else>
        Activity Types
        <span class="drawer-header-small">TOTAL: {{totalNumberOfActivityTypes}}</span>
      </template>
    </h1>

    <div class="edit-delete-buttons-wrapper-full-width">
      <div @click="toggleAllowEditingItemsButtons()" class="edit-txt">edit</div>
      <div @click="toggleAllowDeletingItemsButtons()" class="delete-txt">delete</div>
    </div>

    <div ref="list-of-activity-types" v-if="allActivityTypes" class="list-of-entries-wrapper scrollbar">

      <div class="list-of-entries-header">
        <span class="font-bold">Select Activity type</span>
        <span class="text-sm text-gray-200">Define what you are doing.</span>
      </div>

      <div v-if="allActivityTypes" class="list-of-entries">

        <div
          class="list-entry"
          v-for="item in allActivityTypes"
          :key="item.id"
          :data-id="item.id">

          <label class="list-entry-label">
            <input type="radio" :value="item.id" v-model="currentActivityType">
            <span class="checkmark"></span>
            <span class="ml-2">{{ item.title }}</span>
          </label>

          <div class="edit-delete-icons-wrapper">
            <svg @click="openModal(item.id)" class="delete-icon">
              <use xlink:href="#cross"/>
            </svg>
            <svg @click="editOneItem(item)" class="edit-icon">
              <use xlink:href="#notes"/>
            </svg>
          </div>

        </div>

        <div class="w-full border-t border-pink-700 mt-3 pt-3" :class="{ 'hidden': !sensorsAllowed }">
          <div class="option-wrapper" :class="{ 'cursor-not-allowed opacity-50': recording }">
            <div class="option">
              <span class="font-bold">Can use Pedometer</span>
              <toggle-button
                class="ml-auto"
                color="#059605"
                @change="onChangeUsePedometerOnOff"
                :sync="true"
                :value="usePedometer"
                :disabled="recording === 1"
                :labels="true" />
            </div>
            <div class="option-child__info--wrapper">
              <div class="option-child__info">Activates gyroscope to count steps.</div>
            </div>
          </div>
        </div>

        <!-- Check-uncheck all -->
        <div class="check-uncheck-wrapper">
          <div class="uncheck" @click="uncheckList()" v-if="totalNumberOfActivityTypes > 0">Uncheck All</div>
          <div class="addPresets" @click="reAddInitialData()" v-else>Add presets</div>
        </div>

        <!-- Re-add presets -->
        <div class="addPresetsMsg" v-html="reAddMessage" v-if="reAddingInitialDataSuccessful"></div>

      </div>

    </div>

    <Editor
      :setup=setup
      :mode=mode
      :toggleClass=toggleClass
      :id=id
      :title=title
      :content=content
      :createdAt=createdAt
      :feedback=feedback
      @onAddItem='addItem'
      @onUpdateItem='updateItem'
      @onResetEditor='resetMode'
    />

  </div>
</template>

<script>
import GeoMoverDB from '@/db/indexeddb'
// import Editor from '@/components/Editor'

// TODO: Implement sorting option (drag-and-drop)

export default {
  name: 'ActivityTypes',
  components: {
    Editor: () => import('@/components/Editor')
  },
  data () {
    return {
      //
      // ----- Props for Editor
      //
      setup: 'min', // 'full', 'min'
      // "mode" is used by editor to call upon its state machine
      mode: 'new', // edit, preEdit, delete, editTitle, editContent, createNewTitle
      // this class is put on the body; react to it
      toggleClass: 'activity-types-editor-active',
      // new activity
      id: null,
      title: '',
      content: null, // always empty, but must be defined
      createdAt: null,
      // The message after "save", "cancel" etc. action was (un-)successful
      feedback: null,
      //
      // ----- Local Attributes
      //
      // When editing, save the whole item locally, too, instead of only
      // sending to the editor. This is, because we only edit the "name" of the
      // track in the editor, nothing else. But when we save again, we need the
      // rest of the item's infos.
      editItem: {},
      // Toggle between "edit items" and "delete items". Their status defines,
      // which "mode" is sent to the Editor child component.
      allowDeletingItems: false,
      allowEditingItems: false,
      // delete note
      idOfItemToDelete: null,
      // reAdd
      reAddMessage: null,
      reAddingInitialDataSuccessful: false
    }
  },
  computed: {
    dbReady: {
      // See comment in "TrackedRoutes" at same location
      get () { return this.$store.getters['settings/getDbReady'] }
    },
    allActivityTypes: {
      get () { return this.$store.getters['activityTypes/getAllActivityTypes'] }
    },
    // Specific to lists with select options (radio or checkbuttons);
    // Not applicable for New lists... therefore let's stick with the
    // very specific property name "...ActivityType" (instead of "...Item")
    currentActivityType: {
      get () { return this.$store.getters['activityTypes/getCurrentActivityType'] },
      set (val) {
        this.$store.dispatch('activityTypes/setCurrentActivityType', val)
        this.setCurrentActivityTypeObj()
      }
    },
    totalNumberOfActivityTypes: {
      get () { return this.allActivityTypes.length }
    },
    //
    // Sensors
    //
    sensorsAllowed: {
      get () { return this.$store.getters['settings/getSensorsAllowed'] },
      set (bool) { this.$store.dispatch('settings/setSensorsAllowed', bool) }
    },
    usePedometer: {
      get () { return this.$store.getters['settings/getUsePedometer'] },
      set (bool) { this.$store.dispatch('settings/setUsePedometer', bool) }
    },
    // needed to deactivate the usePedometer toggler, when recording us on
    recording: {
      get () {
        return this.$store.getters['newActivity/getRecording']
      }
    },
    /**
     * @see TrackedRoutes.vue
     * @requires activeMenu watcher
     */
    activeMenu: {
      get () { return this.$store.getters['ui/getActiveMenu'] }
    }
  },
  methods: {
    /**
     * ----- EDITOR
     */
    // Called from Editor child component 'onResetEditor'
    resetMode () {
      this.log = 'ActivityTypes: resetMode invoked'
      this.log = '> this.mode = ' + this.mode + ''
      if (this.mode !== 'delete') {
        this.mode = 'new'
      }
      this.allowEditingItems = false
    },
    toggleAllowEditingItemsButtons () {
      // this.$l.info('ActivityTypes', 'toggleAllowEditingItemsButtons invoked', '')

      document.body.classList.toggle('activity-types-edit')
      document.body.classList.remove('activity-types-delete')

      this.allowEditingItems = !this.allowEditingItems
      this.allowDeletingItems = false

      // "preEdit" does not invoke a state change, so we remain in "idle"
      this.mode = !this.allowEditingItems ? 'new' : 'preEdit'
    },
    toggleAllowDeletingItemsButtons () {
      // this.$l.info('ActivityTypes, 'toggleAllowDeletingItemsButtons invoked','')

      document.body.classList.toggle('activity-types-delete')
      document.body.classList.remove('activity-types-edit')

      this.allowDeletingItems = !this.allowDeletingItems
      this.allowEditingItems = false

      this.mode = !this.allowDeletingItems ? 'new' : 'delete'
      // this.$l.info('ActivityTypes', 'toggleAllowDeletingItemsButtons\nthis.mode =', this.mode)
    },
    /**
     * ----- ADD, EDIT, UPDATE, DELETE --- Interactions with IDB
     */
    editOneItem (item) {
      this.mode = 'editContent' // see Editor -> watch:mode
      this.id = item.id
      this.title = item.title
      this.toggleClass = 'activity-types-editor-active'
      // Just to make sure, save the complete "activity type to be edited"
      this.editItem = item

      // this.$l.debug('ActivityTypes', 'editOneItem\nthis.edit =', this.edit)
    },
    async addItem (obj) {
      // Construct new activity type object
      const newItemObj = {
        title: obj.title,
        createdAt: Date.now()
      }

      // If GeoMoverDB is binded to a restul variable, do not use "await"!
      const result = GeoMoverDB.addOne('activityTypes', newItemObj)
      result
        .then(newActivityId => {
          // "newActivity" would be the returned id of the newly created activity item;
          this.feedback = 'Saved.'
          this.getAllActivityTypes(newActivityId)
        })
        .catch(error => {
          this.$l.error('ActivityTypes', 'addOneItem\nError adding activity type. What now? error =', error)
          this.feedback = 'Error.'
        })
        .finally(() => {
          this.clearFeedbackTimeout()
          this.resetProps()
        })
    },
    async updateItem (obj) {
      // This causes trouble when id = 0, so compare against... what?? Definitely not against 0!!!
      if (obj.id === null) {
        this.feedback = 'No activity selected to update.'
        return false
      }

      // Rebuild the item with the edited content (title and content)
      const editedItemObj = {
        id: obj.id,
        title: obj.title,
        createdAt: obj.createdAt || Date.now(),
        updated: Date.now()
      }

      const result = GeoMoverDB.updateOne('activityTypes', editedItemObj, obj.id)
      result
        .then(success => {
          // this.$l.success('ActivityTypes', 'updateItemUpdate successful, now handle vuex store. Success msg =', success)
          this.feedback = 'Update successful.'
          // Reload all items
          this.getAllActivityTypes()
        })
        .catch(error => {
          this.$l.error('ActivityTypes', 'updateItem\nerror =', error)
          this.feedback = 'Update error.'
        })
        .finally(() => {
          this.clearFeedbackTimeout()
          this.resetProps()
        })
    },
    // TODO: Needs visual feedback for the user
    async deleteItem () {
      // this.$l.debug('ActivityTypes', 'deleteItem\nDeleting item for real, this.idOfItemToDelete =', this.idOfItemToDelete)
      const deleteResult = GeoMoverDB.deleteOne('activityTypes', this.idOfItemToDelete)
      deleteResult
        .then(result => {
          this.getAllActivityTypes()
        })
        .catch(error => {
          this.$l.error(
            'ActivityTypes',
            'deleteItem\nThere was an error in the delete process of an activity type. What now?\nerror =',
            error
          )
        })
    },
    /**
     * ----- FETCH ALL DATA
     */
    async getAllActivityTypes (idOfChangedItem = null) {
      // load all activity types from DB, then save them to the vuex store
      // also calculate current active activity type
      await GeoMoverDB.findAll('activityTypes')
        .then(result => {
          this.$store.dispatch('activityTypes/loadActivityTypes', result)
          this.setCurrentActivityTypeObj()
        })
        .catch(error => {
          this.$store.dispatch('activityTypes/loadActivityTypes', { id: 0, title: 'Error loading activity types! Error = ' + error })
        })

      // If there was an item that was added or update, display a visual indicator
      if (idOfChangedItem !== null) {
        // this.$l.info('ActivityTypes', 'getAllActivityTypes\nan item was change, add avisual indicator to idOfChangedItem =', idOfChangedItem)
        this.indicateChange(idOfChangedItem)
      }
    },
    /**
     * Provide a visual indication for a just recently changed item
     * @param {number|string} id - The id of the item that was changed or added
     */
    indicateChange (id = null) {
      // this.$l.info('ActivityTypes', 'indicateChange\nchanged item id', id)
      if (id != null) {
        const changedItem = document.querySelector('[data-id="' + id + '"]')

        // this.$l.debug('ActivityTypes', 'changedItem =', changedItem)

        // Add a background color
        changedItem.classList.add('item-changed')

        // Remove the bg again after a while
        setTimeout(() => {
          changedItem.classList.remove('item-changed')
        }, 2000)
      }
    },
    /**
     * ----- RESET OPTIONS, CLEANUP
     */
    clearReAddMsgField (dur) {
      setTimeout(() => {
        this.reAddMessage = ''
        this.reAddingInitialDataSuccessful = false
      }, dur)
    },
    resetProps () {
      // these should actually reach down to the editor... but it seems they do not
      this.id = null
      this.title = null
      // this.content = null
      this.createdAt = null
    },
    clearFeedbackTimeout () {
      // "this.mode" is sent to the editor component to change state to "idle"
      setTimeout(() => {
        this.mode = 'idle'
        this.feedback = null
      }, 1000)
    },
    clearInputFields () {
      // Clear Input field
      this.newItemTitle = null
      this.editItemId = null
      this.editItem = null
      // Clear feedback field
      this.feedback = ''
    },
    /**
     * ----- Modal Interaction
     */
    openModal (id) {
      // this.$l.info('ActivityTypes', 'openModal\nItem to delete id =', id)
      // Intermediate saving of id of article, so in case the modal is confirmed,
      // deleting if the article can be executed more easily
      this.idOfItemToDelete = id

      // we must pass object params with all the information
      const params = {
        mode: 'confirm',
        title: 'Attention',
        text: 'Are you sure you want to delete this Activity Type? This action is not undoable!',
        // we are passing callback method for our confirm button
        onConfirm: () => {
          return this.deleteItem()
        }
      }

      this.$modal.show(params)
    },
    cancelDeleteAction () {
      // this.$l.info('ActivityTypes', 'cancelDeleteAction invoked', '')
      this.idOfItemToDelete = null
    },
    /**
     * ----- UI
     */
    uncheckList () {
      this.currentActivityType = -1
    },
    setCurrentActivityTypeObj (val = null) {
      // Currecnt Activity Type ({ id: 0, title: '...' })
      if (val === null) {
        const obj = this.$store.getters['activityTypes/getAllActivityTypes']
        const id = this.$store.getters['activityTypes/getCurrentActivityType']
        const item = obj.find(x => x.id === id)
        this.$store.dispatch('activityTypes/setCurrentActivityTypeObj', item)
      }
      else {
        this.$store.dispatch('activityTypes/setCurrentActivityTypeObj', val)
      }
    },
    async reAddInitialData () {
      const result = GeoMoverDB.reAddStoreData('activityTypes')
      result
        .then(e => {
          this.reAddMessage = 'Added initial data.'
          this.clearReAddMsgField(2500)
          this.reAddingInitialDataSuccessful = true
        })
        .catch(e => {
          this.reAddMessage = '<p class="mb-2">Could not add (all of the) initial data. Probably there is an activity with the same name already existing in your list of activities. Names have to be unique.</p><p class="text-xs">(This message will disappear after 10 seconds.)</p>'
          this.clearReAddMsgField(10000)
        })
        .finally(() => {
          this.getAllActivityTypes()
        })
    },
    //
    // ----- Sensors
    //
    onChangeUsePedometerOnOff () {
      this.$store.dispatch('settings/setUsePedometer', !this.$store.getters['settings/getUsePedometer'])
    }
  },
  mounted () {
    // this.$l.info('ActivityTypes', 'mounted', '')
    this.getAllActivityTypes()
  },
  watch: {
    /**
     * @see TrackedRoutes.vue for explanation (short: reset "deacticate delete" text)
     * @requires activeMenu in computed properties
     */
    activeMenu: function (newVal, oldVal) {
      if (oldVal === 'activity-types') {
        this.mode = 'new'
      }
    },
    dbReady: function (newVal, oldVal) {
      // this.$l.debug('ActivityTypes','watch - dbReady invoked', '')
      // We have no "itemsPerPage" in this component (like in "Notes")
      // so fetch items as soon as db is ready
      this.getAllActivityTypes()
    }
  }
}
</script>

<style lang="postcss" scoped>
/* different from "Notes" and "TrackedRoutes": they need extra padding, here not. */
.drawer-header {
  @apply flex justify-between items-baseline;
  @apply w-full mb-3 text-2xl font-bold select-none;
  /* @apply px-2 pt-2; */
}

/* List of items (select list of activity types) */
/* Again: different than other components */
.list-of-entries-wrapper {
  @apply flex flex-col flex-grow;
  @apply relative w-full overflow-y-scroll;
  @apply pr-1 mb-8;
}

.list-of-entries-header {
  @apply flex flex-col items-start justify-start mb-3 select-none;
}

.list-of-entries {
  @apply relative flex flex-col flex-grow items-start justify-start mb-2 pt-2 border-t border-pink-700;
}

.list-entry {
  @apply flex flex-row w-full items-center leading-snug rounded transition duration-700 bg-transparent bg-opacity-0
}

.list-entry-label {
  @apply relative px-1 py-1 rounded-md w-full flex flex-row items-center transition duration-300;
}
.list-entry-label:hover {
  @apply bg-pink-700;
}

.item-changed {
  @apply transition-colors duration-300 bg-pink-700 bg-opacity-40;
}

/* Check-uncheck buttons */

.check-uncheck-wrapper {
  @apply flex flex-row w-full items-center justify-between mt-3 pt-2 border-t border-pink-700;
}

.addPresets,
.uncheck {
  @apply my-2 px-1 py-0.5;
  @apply rounded;
  @apply text-xs text-pink-500 uppercase font-bold;
  @apply select-none cursor-pointer;
  @apply transition;
}

.uncheck {
  @apply mx-0
}

.addPresets {
  @apply ml-auto
}

.addPresets:hover,
.uncheck:hover {
  @apply text-pink-400
}

.addPresetsMsg {
  @apply block w-full mt-3
}

/* Editor - see index.css */

/**
 * Since we do not use routes and various components for the menu contents (notes,
 * activity Types, settings, etc), we use CSS to show and hide ertain states in order
 * to be able to control the display also from "far away" (by removing the css classes)
 * from the body content). This seems easier then using vuex store variables...
 *
 * If we used routes that loaded separate components, we would use local variables (data)
 * and manage them in a beforeDestroy hook (even though the unloading itself would probably
 * automatically reset the display.
 */

/*
.new { display: flex; }
.edit { display: none; }
.delete { display: none; }

.activity-types-edit .new { display: none; }
.activity-types-edit .edit { display: flex; }
.activity-types-edit .delete { display: none; }

.activity-types-delete .new,
.activity-types-delete .edit,
.activity-types-delete .editor-toggle-icon {
  display: none;
}

.activity-types-delete .delete {
  display: inline-block;
}
*/
</style>
